/**
 * Token issuance progress, derived from the payment row alone.
 *
 * A CRM-generated token is written as a placeholder row with `token: null`, and
 * Pay@ fills it in later over a callback. There is no status column, so the
 * states here are derived from the three fields the row does carry: `token`,
 * `createdAt`, and `description`.
 *
 * `description` is the one that separates a dead request from a slow one. The
 * placeholder is written with it null, and only a settling callback ever fills
 * it in — with the provider's own words. So a row holding a message but no
 * token is one Pay@ has already answered, and the answer was not a token. That
 * is the failure signal ADR 0003 originally said we did not have.
 *
 * The thresholds come from the measured CRM-initiated distribution (n=638,
 * 2026-06-11 → 2026-08-05): median 26.3s, p95 80.2s, p99 87.1s, with the
 * distribution stopping dead at ~90s. Till-originated payments are a different
 * population running at roughly twice the latency, but they can never be
 * pending — their row is written *by* the callback handler, with the token
 * already attached — so they never reach this code.
 *
 * See docs/adr/0003-token-progress-display-timeout-only.md, in particular the
 * rule that TOKEN_STOP_POLLING_MS stops the UI *looking* and must never cancel,
 * fail, or hide the row. Tokens have arrived seven hours late.
 */

/** Don't poll before this. Only 5% of tokens land under 10s; p5 is 9.9s. */
export const TOKEN_FIRST_POLL_MS = 8_000;

/** p95. Past here the wait is genuinely abnormal and the row says so. */
export const TOKEN_SLOW_AFTER_MS = 80_000;

/** Just above the apparent ~90s ceiling. Abandons 0.16% of successes. */
export const TOKEN_STOP_POLLING_MS = 120_000;

/**
 * Past here the request is a straggler, not a wait.
 *
 * Two hours, which is `UNANSWERED_AFTER_MINUTES` in the server's
 * `src/lib/unanswered-tokens.ts` — the same cutoff its 07:30 sweep uses to put
 * a request in front of a person. The two must agree: a row the sweep has
 * already reported as unanswered cannot still be telling the operator looking
 * at it that the token is on its way.
 *
 * This says nothing about failure. A request Pay@ has not answered has not
 * been refused, and ADR 0003's rule that age cannot observe failure stands.
 * What age *can* observe is silence, and past two hours against a provider
 * that answers inside ninety seconds, silence is worth saying.
 */
export const TOKEN_STALLED_AFTER_MS = 2 * 60 * 60 * 1_000;

/** Past the 90s cliff arrivals are vanishingly rare, so the cadence relaxes. */
const POLL_RELAX_AFTER_MS = 90_000;

const POLL_FAST_MS = 4_000;
const POLL_SLOW_MS = 10_000;

export type TokenStepState = 'done' | 'active' | 'stalled' | 'failed' | 'pending';
export type TokenStepId = 'accepted' | 'awaiting' | 'issued';

export interface TokenStep {
  id: TokenStepId;
  state: TokenStepState;
}

export interface TokenProgress {
  steps: TokenStep[];
  /** No token yet and none refused — still genuinely on its way. */
  isPending: boolean;
  /** Still waiting, past p95. */
  isSlow: boolean;
  /** Past the cutoff — we have stopped looking, but the row is still live. */
  hasStoppedLooking: boolean;
  /** Hours on, still no answer of any kind. Nobody is coming unless chased. */
  isStalled: boolean;
  /** Pay@ answered and brought no token. The request is over. */
  hasFailed: boolean;
  /** The deposit took the whole payment. Settled, no token due. */
  isDepositOnly: boolean;
  /** Money that counts with no token on it, for any reason. Nothing to wait for. */
  isSettledWithoutToken: boolean;
  /** What the deposit took of it, where the row says. */
  depositSettled: number | null;
  /** The one line worth reading — see summariseProviderMessage. */
  failureMessage: string | null;
  /** The vendor's untouched text, or null when the summary already is it. */
  failureDetail: string | null;
  elapsedMs: number;
}

interface TokenRow {
  token?: string | null;
  createdAt: string;
  /**
   * Whatever the settling callback said. Null on a placeholder that has not
   * been answered yet, which is what makes it usable as a failure signal.
   */
  description?: string | null;
  /**
   * Whether the money on this row counts. A row with no token but a message
   * looks refused; a row with no token that COUNTS is the one case where that
   * reading is wrong - the payment was taken and booked without one. See
   * `isSettledWithoutToken`.
   */
  countsTowardBalance?: boolean | null;
  /** How much of this payment the outstanding deposit took, in rand. */
  depositDeducted?: number | null;
  /** What the row books, in rand. Only read to tell a deposit-only payment apart. */
  amount?: number | null;
}

/**
 * Money that counts, with no token on the row.
 *
 * A row with no token and a provider message has exactly the shape of a
 * refusal, and a row with no token and no message the shape of a request still
 * waiting. `countsTowardBalance` is the one fact that says neither is true: the
 * server only sets it on money it has booked. Three kinds of payment carry it
 * without a token - the deposit swallowed the payment whole ("No Allocations
 * Found"), the contract had no device to vend against yet, or the payment was
 * migrated from a system whose token was never copied across.
 *
 * Nothing further is coming for such a row and nothing is wrong with it, so it
 * is neither polled nor shown as a failure.
 */
export function isSettledWithoutToken(tx: TokenRow): boolean {
  return tx.token == null && tx.countsTowardBalance === true;
}

/**
 * The one settled-without-token payment whose cause the row proves: the
 * outstanding deposit took all of it. Anything short of that is labelled for
 * what it is rather than for a reason the row cannot show - most of these rows
 * are migrated history, and calling them deposits was simply untrue.
 */
export function isDepositOnly(tx: TokenRow): boolean {
  return (
    isSettledWithoutToken(tx) &&
    tx.depositDeducted != null &&
    tx.amount != null &&
    tx.amount > 0 &&
    tx.depositDeducted >= tx.amount
  );
}

/**
 * Whether a row without a token has a progress card worth showing. A request
 * still in flight does, and so does a deposit-only payment, whose card explains
 * where the money went. Any other settled payment has nothing in progress.
 */
export function hasTokenProgressToShow(tx: TokenRow): boolean {
  if (tx.token != null) return false;
  return !isSettledWithoutToken(tx) || isDepositOnly(tx);
}

/**
 * The provider's message on a row it answered without a token, or null if the
 * row is settled or still unanswered. This is the raw text, exactly as stored.
 */
export function tokenFailureMessage(tx: TokenRow): string | null {
  if (tx.token != null) return null;
  // Money that counts was never a refusal, whatever the provider's words.
  if (isSettledWithoutToken(tx)) return null;
  const message = tx.description?.trim();
  return message ? message : null;
}

/** A JSON `"Message": "…"` pair anywhere in the text, escapes and all. */
const JSON_MESSAGE = /"message"\s*:\s*"((?:[^"\\]|\\.)*)"/gi;

/** Undo JSON string escaping on a captured value, or leave it alone if it is
 *  not something JSON can read back. */
function decodeJsonString(value: string): string {
  try {
    return JSON.parse(`"${value}"`) as string;
  } catch {
    return value;
  }
}

/** The prose a vendor writes before the machine payload it appends. */
function proseBeforePayload(raw: string): string {
  const brace = raw.indexOf('{');
  return (brace === -1 ? raw : raw.slice(0, brace)).trim();
}

/**
 * The one sentence worth putting in front of an operator, pulled out of what
 * the vendor actually sent.
 *
 * What arrives is prose with a serialised result glued to the end:
 *
 *   Token generation failed via AtlasIndependent AtlasIndependent.RequestToken()
 *   Error Validating account{ "Item1": -1, "Item2": { "AccountNumber":
 *   "842478655", "Valid": false, "Message": "Account is not valid." } }
 *
 * Everything an operator can act on is in the innermost `Message` — "Account is
 * not valid." The rest names the vendor's own method and repeats what the row
 * already shows. So the deepest `Message` wins: vendors wrap the specific
 * reason inside the general one, which makes the last match the precise one.
 *
 * Nothing is thrown away — the full text stays on the card behind a disclosure,
 * because anyone taking this to Pay@ needs the words Pay@ used.
 */
export function summariseProviderMessage(raw: string): string {
  const matches = [...raw.matchAll(JSON_MESSAGE)];
  const deepest = matches.at(-1)?.[1];
  if (deepest) {
    const decoded = decodeJsonString(deepest).trim();
    if (decoded) return decoded;
  }
  // No message inside the payload, so the prose in front of it is the best we
  // have; a message with no payload at all is already the whole story.
  return proseBeforePayload(raw) || raw.trim();
}

/**
 * How a row reads at a glance, for the callers that show a badge and a tint
 * rather than the whole step list.
 *
 * It exists so the tabs and the progress card cannot drift apart. Both were
 * deciding "pending or refused?" with their own ternary, and adding silence as
 * a third outcome to two copies of that would have been two chances to get it
 * wrong — a row cannot say "Token pending" beside a card that says nobody
 * answered.
 */
export type TokenOutcome = 'issued' | 'deposit' | 'paidNoToken' | 'pending' | 'stalled' | 'refused';

export function tokenOutcome(tx: TokenRow, now: number): TokenOutcome {
  if (tx.token != null) return 'issued';
  if (isDepositOnly(tx)) return 'deposit';
  if (isSettledWithoutToken(tx)) return 'paidNoToken';
  if (tokenFailureMessage(tx) !== null) return 'refused';
  return ageMs(tx.createdAt, now) >= TOKEN_STALLED_AFTER_MS ? 'stalled' : 'pending';
}

/**
 * Is this row still worth watching? Only a placeholder Pay@ has not answered
 * is — a refused one will not change again on its own, so it earns neither a
 * clock tick nor a poll.
 */
function isAwaitingToken(tx: TokenRow): boolean {
  return tx.token == null && tokenFailureMessage(tx) === null && !isSettledWithoutToken(tx);
}

/**
 * Clamped at zero: a server clock running ahead of the browser's would
 * otherwise produce a negative age and read as "too fresh to poll" forever.
 * An unparseable date does the same, which fails closed rather than polling
 * against a row we cannot age.
 */
function ageMs(createdAt: string, now: number): number {
  const started = Date.parse(createdAt);
  if (Number.isNaN(started)) return 0;
  return Math.max(0, now - started);
}

export function deriveTokenProgress(tx: TokenRow, now: number): TokenProgress {
  const elapsedMs = ageMs(tx.createdAt, now);
  const rawFailure = tokenFailureMessage(tx);
  const hasFailed = rawFailure !== null;
  const failureMessage = rawFailure === null ? null : summariseProviderMessage(rawFailure);
  const depositOnly = isDepositOnly(tx);
  const settledWithoutToken = isSettledWithoutToken(tx);
  const isPending = tx.token == null && !hasFailed && !settledWithoutToken;
  const isStalled = isPending && elapsedMs >= TOKEN_STALLED_AFTER_MS;

  return {
    // The row only exists because Pay@ acknowledged the request, so the first
    // step is proven by the row's own existence. The refusal lands on the
    // second step, because that is the one that was being waited on; the third
    // stays untouched, since no token was ever issued to tick it.
    //
    // Silence lands on the second step too, for the same reason, but as its
    // own state rather than as a refusal: a request Pay@ declined and one it
    // never answered need different things done about them, so they must not
    // look the same. The third step stays pending either way — it is still
    // true that no token has been issued, and nothing here rules one out.
    //
    // A deposit-only payment ends the story well: every step is done, the last
    // one included. Nothing is outstanding — the money was taken, the deposit
    // took it, and no token was ever due — so leaving the final step hollow
    // would show an unfinished journey that is in fact finished. What it says
    // changes instead of what it looks like, and that is the caller's job.
    steps: [
      { id: 'accepted', state: 'done' },
      {
        id: 'awaiting',
        state: hasFailed ? 'failed' : isStalled ? 'stalled' : isPending ? 'active' : 'done',
      },
      { id: 'issued', state: tx.token != null || settledWithoutToken ? 'done' : 'pending' },
    ],
    isPending,
    isSlow: isPending && elapsedMs >= TOKEN_SLOW_AFTER_MS,
    hasStoppedLooking: isPending && elapsedMs >= TOKEN_STOP_POLLING_MS,
    isStalled,
    hasFailed,
    isDepositOnly: depositOnly,
    isSettledWithoutToken: settledWithoutToken,
    depositSettled: depositOnly ? (tx.depositDeducted ?? null) : null,
    failureMessage,
    // Offering "show the full message" when the full message is what is
    // already on screen would be a button that does nothing.
    failureDetail: rawFailure !== null && rawFailure !== failureMessage ? rawFailure : null,
    elapsedMs,
  };
}

/** How often a single row of this age wants polling. 0 means "not now". */
export function tokenPollInterval(elapsedMs: number): number {
  if (elapsedMs < TOKEN_FIRST_POLL_MS) return 0;
  if (elapsedMs >= TOKEN_STOP_POLLING_MS) return 0;
  return elapsedMs < POLL_RELAX_AFTER_MS ? POLL_FAST_MS : POLL_SLOW_MS;
}

/**
 * The tightest cadence any pending row currently wants, or 0 if none do.
 *
 * One list query serves every row, so the fastest requester wins — a row that
 * is still too fresh to poll simply contributes nothing until it ages past
 * TOKEN_FIRST_POLL_MS. A row Pay@ refused contributes nothing ever.
 */
export function pendingPollInterval(items: readonly TokenRow[], now: number): number {
  let interval = 0;
  for (const item of items) {
    if (!isAwaitingToken(item)) continue;
    const wanted = tokenPollInterval(ageMs(item.createdAt, now));
    if (wanted === 0) continue;
    if (interval === 0 || wanted < interval) interval = wanted;
  }
  return interval;
}

/**
 * Is any pending row still short of the cutoff? Drives the one-second clock:
 * once every pending row is past it — or refused outright — nothing on screen
 * can change again without a fetch, so the clock stops.
 */
export function hasLivePendingToken(items: readonly TokenRow[], now: number): boolean {
  return items.some(
    item => isAwaitingToken(item) && ageMs(item.createdAt, now) < TOKEN_STOP_POLLING_MS
  );
}
